// src/components/public/HeroLogin.js
// Homepage hero with the sign-in form. Colours come from the public theme;
// the theme's `layout` picks a side panel or a card floating on the photo.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eyebrow, usePublicTheme } from './PublicLayout';
import PasswordResetModal from './PasswordResetModal';

const HERO_IMAGE = '/images/hero-carrier.jpg';

const heroBackground = (midShade) => ({
  // Overlay uses the theme's page colour so the photo blends into any theme.
  // The left-to-right layer keeps the headline readable where it sits over the trucks.
  backgroundImage: `linear-gradient(90deg, rgb(var(--page-rgb) / 0.78) 0%, rgb(var(--page-rgb) / 0.5) 38%, rgb(var(--page-rgb) / 0) 70%), linear-gradient(180deg, rgb(var(--page-rgb) / 0.62) 0%, rgb(var(--page-rgb) / ${midShade}) 45%, rgb(var(--page-rgb) / 0.62) 100%), url(${HERO_IMAGE}), linear-gradient(160deg, var(--band-top) 0%, var(--page) 70%)`,
  backgroundSize: 'cover',
  backgroundPosition: 'center 75%',
});

const HeroCopy = ({ navigate }) => (
  <div>
    <Eyebrow>Built for the way you move</Eyebrow>
    <h1 className="mt-5 text-5xl sm:text-7xl font-extrabold tracking-tight leading-[0.95] text-white">
      More Load,
      <span className="block pub-accent">Less time.</span>
    </h1>
    <p className="mt-6 max-w-xl text-lg sm:text-2xl text-slate-100 leading-snug">
      Loads, drivers, routes and finances in one place.
      <span className="block">Less switching. More control.</span>
    </p>
    <button
      onClick={() => navigate('/features')}
      className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-lg border-2 pub-accent-border text-white font-medium pub-hover-tint transition"
    >
      See what it does <i className="fas fa-arrow-right" />
    </button>
  </div>
);

const HeroSteps = () => (
  <div className="mt-16 grid grid-cols-3 max-w-3xl">
    {['Plan smarter', 'Save time', 'Stay in control'].map((t, i) => (
      <div key={t} className={`flex items-center gap-3 sm:gap-5 ${i > 0 ? 'pl-3 sm:pl-8 border-l border-white/30' : ''} pr-3`}>
        <span className="text-2xl sm:text-4xl font-light pub-accent">0{i + 1}</span>
        <span className="text-sm sm:text-xl text-white">{t}</span>
      </div>
    ))}
  </div>
);

const LoginCard = ({ form, onChange, onSubmit, isLoading, error, mode, setMode, navigate }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const isCarrier = mode === 'carrier';

  const modeButton = (value, icon, label) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      aria-pressed={mode === value}
      className={`flex-1 flex items-center justify-center gap-3 py-2.5 rounded-md font-medium transition ${
        mode === value ? 'pub-btn text-white shadow-sm' : 'pub-muted hover:opacity-80'
      }`}
    >
      <i className={`fas ${icon}`} />
      {label}
    </button>
  );

  const field = 'pub-input w-full py-3.5 rounded-lg transition';

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="pub-text text-3xl sm:text-4xl font-extrabold tracking-tight">Sign in</h2>
      <p className="pub-muted mt-2 text-lg">
        {isCarrier ? 'Manage your loads, drivers and pay.' : 'Post vehicles and compare carrier bids.'}
      </p>

      <div className="pub-input mt-7 flex p-1 rounded-lg">
        {modeButton('carrier', 'fa-truck', 'Carrier')}
        {modeButton('dealer', 'fa-car', 'Dealer')}
      </div>

      <form onSubmit={onSubmit} className="mt-7 space-y-5">
        {error && (
          <div role="alert" className="pub-error px-4 py-3 rounded-lg text-sm font-semibold">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="login-email" className="pub-text block font-semibold mb-2">Email</label>
          <div className="relative">
            <i className="fas fa-envelope pub-faint absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              id="login-email"
              type="email"
              name="email"
              autoComplete="email"
              value={form.email}
              onChange={onChange}
              required
              placeholder="you@company.com"
              className={`${field} pl-12 pr-4`}
            />
          </div>
        </div>

        <div>
          <label htmlFor="login-password" className="pub-text block font-semibold mb-2">Password</label>
          <div className="relative">
            <i className="fas fa-lock pub-faint absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              value={form.password}
              onChange={onChange}
              required
              placeholder="••••••••"
              className={`${field} pl-12 pr-12`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="pub-faint absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:opacity-80"
            >
              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="pub-muted flex items-center gap-3">
            <input type="checkbox" className="pub-check h-5 w-5 rounded" />
            Remember me
          </label>
          <button type="button" onClick={() => setShowReset(true)} className="pub-link font-medium">
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 py-4 rounded-lg pub-btn text-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading
            ? <><i className="fas fa-spinner fa-spin" />Signing in…</>
            : <>Sign in <i className="fas fa-arrow-right" /></>}
        </button>
      </form>

      <div className="pub-line pub-muted mt-7 pt-5 border-t text-center">
        No account?{' '}
        <button onClick={() => navigate(isCarrier ? '/register' : '/dealer-register')} className="pub-link font-semibold">
          {isCarrier ? 'Register as a carrier' : 'Register as a dealer'}
        </button>
      </div>
      <div className="pub-muted mt-3 text-center">
        Can't sign in?{' '}
        <button onClick={() => navigate('/contact')} className="pub-link font-semibold">
          Contact support
        </button>
      </div>

      {showReset && (
        <PasswordResetModal initialEmail={form.email} onClose={() => setShowReset(false)} />
      )}
    </div>
  );
};

const HeroLogin = (props) => {
  const navigate = useNavigate();
  const theme = usePublicTheme();
  const card = <LoginCard navigate={navigate} {...props} />;

  if (theme.layout === 'float') {
    return (
      <section className="relative overflow-hidden lg:min-h-[calc(100vh-5rem)] px-6 sm:px-12 lg:px-20 py-14" style={heroBackground(0.22)}>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_440px] gap-12 items-center lg:min-h-[calc(100vh-12rem)]">
          <div className="flex flex-col justify-between h-full">
            <HeroCopy navigate={navigate} />
            <HeroSteps />
          </div>
          <div className="pub-surface pub-glass pub-line border rounded-2xl shadow-2xl shadow-black/40 p-8 sm:p-10">{card}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="grid lg:grid-cols-[1.8fr_1fr]">
      <div
        className="relative overflow-hidden flex flex-col justify-between min-h-[560px] lg:min-h-[calc(100vh-5rem)] px-6 sm:px-12 lg:px-20 pt-14 pb-10"
        style={heroBackground(0.12)}
      >
        <HeroCopy navigate={navigate} />
        <HeroSteps />
      </div>
      <div className="pub-surface pub-line lg:border-l px-6 sm:px-12 lg:px-14 py-14 flex flex-col justify-center">
        {card}
      </div>
    </section>
  );
};

export default HeroLogin;
